import React, {useEffect, useState} from 'react';
import {Box, Typography} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import moment from 'moment';

import {apiGetSupportCustomers, apiGetFarmFieldsWithLogs} from 'constants/Api';
import {ISupportCustomer} from 'constants/interfaces';

type FarmerLogSummary = {
  farmerId: number;
  farmerName: string;
  farmName?: string;
  noteCount: number;
  lastNoteAt?: string;
};

export default function DataCapturingFeedWidget(): React.ReactElement {
  const [rows, setRows] = useState<FarmerLogSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let alive = true;

    apiGetSupportCustomers()
      .then(async (customers) => {
        const withFarms = customers
          .map((c) => ({customer: c, farmId: c.farmer?.farm?.id}))
          .filter(
            (x): x is {customer: ISupportCustomer; farmId: number} =>
              !!x.farmId,
          );

        const summaries = await Promise.all(
          withFarms.map(async ({customer, farmId}) => {
            const farmerName =
              `${customer.farmer.name} ${customer.farmer.surname}`.trim();
            try {
              const fields = await apiGetFarmFieldsWithLogs(farmId);
              const notes = fields.flatMap((f) => f.notes ?? []);
              const lastNoteAt = notes
                .map((n) => n.createdAt)
                .filter((d): d is string => !!d)
                .sort()
                .pop();

              const summary: FarmerLogSummary = {
                farmerId: customer.farmer.id,
                farmerName,
                farmName: customer.farmer.farm?.name,
                noteCount: notes.length,
                lastNoteAt,
              };
              return summary;
            } catch (err) {
              console.error(
                `Failed to load field logs for farmer ${farmerName}`,
                err,
              );
              return null;
            }
          }),
        );

        if (!alive) return;

        const top5 = summaries
          .filter((s): s is FarmerLogSummary => !!s && s.noteCount > 0)
          .sort((a, b) => b.noteCount - a.noteCount)
          .slice(0, 5);

        setRows(top5);
      })
      .catch((err) => {
        console.error('Failed to load data capturing feed for widget', err);
        if (alive) setUnavailable(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  return (
    <Box display="flex" flexDirection="column">
      <Typography className="widget-heading">DATA CAPTURING FEED</Typography>

      {loading ? (
        Array.from({length: 5}).map((_, i) => (
          <Skeleton key={i} variant="text" height={28} />
        ))
      ) : unavailable ? (
        <Typography variant="body2" color="textSecondary">
          Could not load data capturing feed.
        </Typography>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No recent data capturing activity found.
        </Typography>
      ) : (
        rows.map((row, index) => (
          <Box
            key={row.farmerId}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            {/* Rank badge */}
            <Typography
              variant="caption"
              style={{
                fontWeight: 700,
                minWidth: 20,
                color: index === 0 ? '#c62828' : '#757575',
              }}
            >
              #{index + 1}
            </Typography>

            {/* Farmer / farm name */}
            <Box flex={1} mx={1}>
              <Typography variant="body2">{row.farmerName}</Typography>
              {row.farmName && (
                <Typography variant="caption" color="textSecondary">
                  {row.farmName}
                </Typography>
              )}
            </Box>

            {/* Note count + recency */}
            <Box textAlign="right">
              <Typography variant="body2">
                <strong>{row.noteCount}</strong> logs
              </Typography>
              {row.lastNoteAt && (
                <Typography variant="caption" color="textSecondary">
                  {moment(row.lastNoteAt).fromNow()}
                </Typography>
              )}
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
}
